import { useState } from "react";
import {
  Box,
  Menu,
  MenuItem,
  Typography,
  Divider,
} from "@mui/material";
import AssuredWorkloadIcon from '@mui/icons-material/AssuredWorkload';
import {
  Dashboard,
  Business,
  ShoppingCart,
  AccountBalance,
  People,
  BarChart,
  KeyboardArrowDown,
  KeyboardArrowRight,
  Settings,
  AccountTree,
  Inventory2,
  Savings,
  MoreHoriz,
  BalanceOutlined,
  CategoryOutlined,
  LabelOutlined,
  Assignment,
  LocalShipping,
  AssignmentReturn,
  Summarize,
  ReceiptLong,
  PersonSearch,
  ViewList,
  RequestQuote,
  ShoppingBag,
  Undo,
  BarChartOutlined,
  GroupWork,
  PeopleAlt,
  ListAlt,
  TableChart,
  MoveToInbox,
  Outbox,
  CompareArrows,
  Timeline,
  Receipt,
  SwapHoriz,
  PrecisionManufacturing,
  CardGiftcard,
  Build,
  CalendarMonth,
} from "@mui/icons-material";
import { NavLink, useHistory, useLocation } from "react-router-dom";
import { useAuth } from "./AuthContext";

const HorizontalNavbar = () => {
  const history = useHistory();
  const location = useLocation();
  const { permissions, roleName, isSuperAdmin } = useAuth();

  const isDriverRole = (roleName || '').toLowerCase() === 'driver';
  const isExecutiveRole = (roleName || '').toLowerCase().includes('executive');
  const [anchorEl, setAnchorEl] = useState(null);
  const [activeMenu, setActiveMenu] = useState(null);
  const [nestedAnchorEl, setNestedAnchorEl] = useState(null);
  const [activeNestedMenu, setActiveNestedMenu] = useState(null);

  const hasReadMasterPermission = isSuperAdmin || permissions.includes('Read Master');
  const hasReadTransactionPermission = isSuperAdmin || permissions.includes('Read Transaction');
  const hasReadAccountsPermission = isSuperAdmin || permissions.includes('Read Accounts');
  const hasReadAdministrationPermission = isSuperAdmin || permissions.includes('Read Administration');
  const hasReadReportsPermission = isSuperAdmin || permissions.includes('Read Reports');

  const { companyName, username, isAuthenticated } = useAuth();

  const handleMenuOpen = (event, menuId) => {
    setAnchorEl(event.currentTarget);
    setActiveMenu(menuId);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setActiveMenu(null);
    setNestedAnchorEl(null);
    setActiveNestedMenu(null);
  };

  const handleNestedMenuOpen = (event, menuLabel) => {
    event.stopPropagation();
    // Always set the new anchor and menu - this handles switching between submenus
    setNestedAnchorEl(event.currentTarget);
    setActiveNestedMenu(menuLabel);
  };

  const handleNestedMenuClose = () => {
    setNestedAnchorEl(null);
    setActiveNestedMenu(null);
  };

  const handleNavigation = (path) => {
    history.push(path);
    handleMenuClose();
  };

  // Check if a menu item is active based on current route
  const REPORT_ROUTES = [
    '/ReportsHub', '/TrialBalanceReport', '/RatioAnalysisReport', '/SchemeSummaryReport',
    '/ProfitLossMonthWise', '/BalanceSheet', '/BalanceSheetTView',
    '/CashFlowStatement', '/FundFlowStatement',
    '/SalesReport', '/SalesOrderReport', '/CustomerWiseSalesReport',
    '/CustomerAgeingReport', '/SalesRegister', '/SalesAnalysis',
    '/PurchaseReport', '/PurchaseOrderStatusReport', '/VendorandItemWisePurchaseReport',
    '/VendorAgeingReport', '/PurchaseRegister',
    '/StockTransferReport', '/StockMovementReport', '/StockSummaryReport',
    '/Ledger', '/CashBook', '/IncomeExpenseReport', '/ExpenseAdvancedReport',
    '/DetailedInvoice', '/SaleGstReport', '/GSTR3BReport', '/GSTR3BFormatReport',
    '/TaxRegisterSale', '/TaxRegisterPurchase', '/HSNTaxSummary',
    '/DayBookReport', '/DayBookSummaryReport', '/BankBookReport',
    '/DelayedPaymentReport', '/DelayedCollectionReport',
  ];

  const isMenuActive = (item) => {
    const path = location.pathname;
    if (item.id === 'reports') return REPORT_ROUTES.some(r => path.startsWith(r));
    if (item.route) return path === item.route || path.startsWith(item.route);
    if (item.subItems) {
      return item.subItems.some(sub => {
        if (sub.route) return path === sub.route || path.startsWith(sub.route);
        if (sub.subItems) return sub.subItems.some(s => path === s.route || path.startsWith(s.route));
        return false;
      });
    }
    return false;
  };

  const menuItems = [
    {
      id: 'dashboard',
      label: 'Dashboard',
      icon: <Dashboard sx={{ fontSize: 18 }} />,
      route: '/Home',
      permission: true,
    },
    {
      id: 'master',
      label: 'Master',
      icon: <Business sx={{ fontSize: 18 }} />,
      permission: hasReadMasterPermission && !isDriverRole && !isExecutiveRole,
      // subItems: [
      //   { label: 'Company', route: '/CompanyEdit' },
      //   { label: 'Client', route: '/ClientIndex' },
      //   { label: 'Bank', route: '/BankDashboard' },
      //   { label: 'Staff', route: '/StaffDashboard' },
      //   { label: 'Chart of Accounts', route: '/AccountHeadDashboard' },
      //   {
      //     label: 'Master',
      //     isNested: true,
      //     subItems: [
      //       { label: 'Client', route: '/ClientIndex' },
      //       { label: 'Vendor', route: '/VendorIndex' },
      //     ]
      //   },
      // ],
      subItems: [
        {
          label: 'Account Master',
          isNested: true,
          subItems: [
            { label: 'Account Head', route: '/AccountHeadIndex', icon: <AccountTree sx={{ fontSize: 15 }} /> },
            { label: 'Asset', route: '/AssetIndex', icon: <Inventory2 sx={{ fontSize: 15 }} /> },
            { label: 'Capital', route: '/CapitalIndex', icon: <Savings sx={{ fontSize: 15 }} /> },
            { label: 'Others', route: '/OtherOptionIndex', icon: <MoreHoriz sx={{ fontSize: 15 }} /> },
            { label: 'Sales Executive', route: '/SalesExecutiveIndex', icon: <PeopleAlt sx={{ fontSize: 15 }} /> },
            // { label: 'Salesman Dashboard', route: '/SalesManDashboard', icon: <Dashboard sx={{ fontSize: 15 }} /> },
            //  { label: 'Executive Dashboard', route: '/SalesExecutiveDashboard', icon: <Dashboard sx={{ fontSize: 15 }} /> },
            { label: 'Balance Sheet', route: '/BalanceSheetHeadIndex', icon: <BalanceOutlined sx={{ fontSize: 15 }} /> },
          ]
        },
        { label: 'Bank', route: '/BankIndex' },
        { label: 'Company', route: '/CompanyEdit' },
        {
          label: 'Master',
          isNested: true,
          subItems: [
            { label: 'Client', route: '/ClientIndex', icon: <People sx={{ fontSize: 15 }} /> },
            { label: 'Vendor', route: '/VendorIndex', icon: <Business sx={{ fontSize: 15 }} /> },
          ]
        },
        {
          label: 'Product',
          isNested: true,
          subItems: [
            { label: 'Category', route: '/ProductCategoryIndex', icon: <CategoryOutlined sx={{ fontSize: 15 }} /> },
            { label: 'Sub-Category', route: '/ProductSubCategoryIndex', icon: <CategoryOutlined sx={{ fontSize: 15 }} /> },
            { label: 'Brand', route: '/ProductBrandIndex', icon: <LabelOutlined sx={{ fontSize: 15 }} /> },
            { label: 'Item', route: '/ProductItemIndex', icon: <Inventory2 sx={{ fontSize: 15 }} /> },
          ]
        },

        // { label: 'Staff', route: '/StaffDashboard' },

      ],
    },
    {
      id: 'transaction',
      label: 'Transaction',
      icon: <ShoppingCart sx={{ fontSize: 18 }} />,
      permission: (hasReadTransactionPermission || hasReadMasterPermission) && !isDriverRole && !isExecutiveRole,
      subItems: [
        {
          label: 'Purchase',
          isNested: true,
          subItems: [
            { label: 'Purchase Order', route: '/PurchaseOrderIndex', icon: <Assignment sx={{ fontSize: 15 }} /> },
            { label: 'Purchase', route: '/PurchaseIndex', icon: <LocalShipping sx={{ fontSize: 15 }} /> },
            { label: 'Purchase Return', route: '/PurchaseReturnAgainstIndex', icon: <AssignmentReturn sx={{ fontSize: 15 }} /> },
            // { label: 'Purchase Return', route: '/PurchaseReturnIndex' },
            { isDivider: true, label: 'Reports' },
            { label: 'Purchase Report', route: '/PurchaseReport', icon: <Summarize sx={{ fontSize: 15 }} /> },
            { label: 'PO Status Report', route: '/PurchaseOrderStatusReport', icon: <ReceiptLong sx={{ fontSize: 15 }} /> },
            { label: 'Vendor & Item Wise Purchase', route: '/VendorandItemWisePurchaseReport', icon: <PersonSearch sx={{ fontSize: 15 }} /> },
            // { label: 'Vendor Ageing', route: '/VendorAgeingReport', icon: <ReceiptLong sx={{ fontSize: 15 }} /> },
            // { label: 'Item Wise Purchase', route: '/ItemWisePurchaseReport', icon: <ViewList sx={{ fontSize: 15 }} /> },
            { label: 'Purchase Register', route: '/PurchaseRegister', icon: <BarChart sx={{ fontSize: 15 }} /> },
          ]
        },
        {
          label: 'Sales',
          isNested: true,
          subItems: [
            { label: 'Enquiry', route: '/EnquiryIndex', icon: <Assignment sx={{ fontSize: 15 }} /> },
            { label: 'Schemes', route: '/SchemeIndex', icon: <CardGiftcard sx={{ fontSize: 15 }} /> },
            { label: 'Quotation', route: '/QuotationIndex', icon: <RequestQuote sx={{ fontSize: 15 }} /> },
            { label: 'Sales Order', route: '/SaleOrderIndex', icon: <Assignment sx={{ fontSize: 15 }} /> },
            { label: 'Sales', route: '/SaleIndex', icon: <ShoppingBag sx={{ fontSize: 15 }} /> },
            { label: 'Sales Return', route: '/SalesReturnIndex', icon: <Undo sx={{ fontSize: 15 }} /> },
            { isDivider: true, label: 'Reports' },
            { label: 'Sales Report', route: '/SalesReport', icon: <Summarize sx={{ fontSize: 15 }} /> },
            { label: 'SO Status Report', route: '/SalesOrderReport', icon: <ReceiptLong sx={{ fontSize: 15 }} /> },
            { label: 'Customer & Item Wise Sales', route: '/CustomerWiseSalesReport', icon: <PeopleAlt sx={{ fontSize: 15 }} /> },
            // { label: 'Customer Ageing', route: '/CustomerAgeingReport', icon: <ReceiptLong sx={{ fontSize: 15 }} /> },
            // { label: 'Item Wise Sales', route: '/ItemWiseSalesReport', icon: <ListAlt sx={{ fontSize: 15 }} /> },
            { label: 'Sales Register', route: '/SalesRegister', icon: <BarChart sx={{ fontSize: 15 }} /> },
            // { label: 'Sales Summary', route: '/SalesSummaryReport', icon: <TableChart sx={{ fontSize: 15 }} /> },
          ]
        },
        {
          label: 'Stock',
          isNested: true,
          subItems: [
            { label: 'Stock Transfer In', route: '/StockTransferInIndex', icon: <MoveToInbox sx={{ fontSize: 15 }} /> },
            { label: 'Stock Transfer Out', route: '/StockTransferOutIndex', icon: <Outbox sx={{ fontSize: 15 }} /> },
            { isDivider: true, label: 'Reports' },
            { label: 'Stock Transfer Report', route: '/StockTransferReport', icon: <CompareArrows sx={{ fontSize: 15 }} /> },
            { label: 'Stock Movement Report', route: '/StockMovementReport', icon: <Timeline sx={{ fontSize: 15 }} /> },
          ]
        },
        { label: 'Production', route: '/ProductionIndex', icon: <PrecisionManufacturing sx={{ fontSize: 15 }} /> },
        { label: 'E-Invoice', route: '/EInvoiceIndex', icon: <Receipt sx={{ fontSize: 15 }} /> },
        { label: 'GST Item Bulk Update', route: '/GSTItemBulkUpdate', icon: <SwapHoriz sx={{ fontSize: 15 }} /> },
      ],
    },
    {
      id: 'accounts',
      label: 'Accounts',
      icon: <AccountBalance sx={{ fontSize: 18 }} />,
      permission: hasReadAccountsPermission && !isDriverRole && !isExecutiveRole,
      subItems: [
        { label: 'Payment & Receipt', route: '/PaymentAndReceiptIndex' },
        { label: 'Journal Entry', route: '/JournalEntryIndex' },
      ],
    },
    // {
    //   id: 'delivery',
    //   label: 'Delivery',
    //   icon: <LocalShipping sx={{ fontSize: 18 }} />,
    //   permission: true,
    //   //permission: roleName !== "Driver",
    //   subItems: [
    //     { label: 'Schedules',             route: '/Schedules',                icon: <CalendarMonth sx={{ fontSize: 15 }} /> },
    //     { label: 'Installation',          route: '/InstallationScheduleList', icon: <Build sx={{ fontSize: 15 }} /> },
    //     { label: 'Onsite Exchange Item',  route: '/OnsiteExchangeList',       icon: <SwapHoriz sx={{ fontSize: 15 }} /> },
    //     { label: 'Cash On Delivery',      route: '/CashOnDeliveryList',       icon: <LocalShipping sx={{ fontSize: 15 }} /> },
    //   ],
    // },

    //  {
    //   id: 'Updates',
    //   label: 'Delivery Updates',
    //   icon: <LocalShipping sx={{ fontSize: 18 }} />,
    //   permission: true,
    //   // permission: roleName === "Driver",
    //   subItems: [
    //     { label: 'Onsite Exchange',  route: '/ExchangeList',       icon: <SwapHoriz sx={{ fontSize: 15 }} /> },
    //     { label: 'Cash On Delivery',      route: '/CODList',       icon: <LocalShipping sx={{ fontSize: 15 }} /> },
    //   ],
    // },

    {
      id: 'delivery',
      label: 'Delivery',
      icon: <LocalShipping sx={{ fontSize: 18 }} />,
      permission: roleName !== "Driver" && !isExecutiveRole,
      subItems: [
        { label: 'Schedules', route: '/Schedules', icon: <CalendarMonth sx={{ fontSize: 15 }} /> },
        { label: 'Installation', route: '/InstallationScheduleList', icon: <Build sx={{ fontSize: 15 }} /> },
        { label: 'Onsite Exchange Item', route: '/OnsiteExchangeList', icon: <SwapHoriz sx={{ fontSize: 15 }} /> },
        { label: 'Cash On Delivery', route: '/CashOnDeliveryList', icon: <LocalShipping sx={{ fontSize: 15 }} /> },
      ],
    },
    {
      id: 'updates',
      label: 'Delivery Updates',
      icon: <LocalShipping sx={{ fontSize: 18 }} />,
      permission: roleName === "Driver",
      subItems: [
        { label: 'Onsite Exchange', route: '/ExchangeList', icon: <SwapHoriz sx={{ fontSize: 15 }} /> },
        { label: 'Cash On Delivery', route: '/CODList', icon: <LocalShipping sx={{ fontSize: 15 }} /> },
      ],
    },

    {
      id: 'enquiry',
      label: 'Enquiry',
      icon: <Assignment sx={{ fontSize: 18 }} />,
      route: '/EnquiryIndex',
      permission: isExecutiveRole,
    },
    {
      id: 'reports',
      label: 'Reports',
      icon: <BarChart sx={{ fontSize: 18 }} />,
      route: '/ReportsHub',
      permission: (hasReadReportsPermission || hasReadMasterPermission) && !isDriverRole && !isExecutiveRole,
    },
    {
      id: 'auth',
      label: 'Auth',
      icon: <People sx={{ fontSize: 18 }} />,
      permission: hasReadAdministrationPermission && !isDriverRole && !isExecutiveRole,
      subItems: [
        { label: 'Users', route: '/UserIndex' },
        { label: 'Roles', route: '/RoleIndex' },
        { label: 'Reset Password', route: '/PasswordChange' },
        // { label: 'Audit Log', route: '/AuditLogIndex', icon: <AssuredWorkloadIcon sx={{ fontSize: 15 }} /> },
      ],
    },
    {
      id: 'settings',
      label: 'Settings',
      icon: <Settings sx={{ fontSize: 18 }} />,
      permission: hasReadMasterPermission,
      subItems: [
        { label: 'Logout', route: '/Logout' },
      ],
    },
  ];

  const renderNestedMenu = (parentItem) => (
    <Menu
      anchorEl={nestedAnchorEl}
      open={activeNestedMenu === parentItem.label}
      onClose={handleNestedMenuClose}
      anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      TransitionProps={{ timeout: 180 }}
      PaperProps={{
        elevation: 8,
        sx: {
          backgroundColor: '#1e2d5a',
          color: 'white',
          minWidth: '230px',
          maxHeight: '70vh',
          overflowY: 'auto',
          ml: 0.5,
          borderRadius: '8px',
          border: '1px solid rgba(255,255,255,0.08)',
          boxShadow: '0 8px 32px rgba(0,0,0,0.45)',
          '&::-webkit-scrollbar': { width: '4px' },
          '&::-webkit-scrollbar-track': { background: 'transparent' },
          '&::-webkit-scrollbar-thumb': { background: 'rgba(255,255,255,0.2)', borderRadius: '4px' },
        },
      }}
    >
      {parentItem.subItems?.map((subItem, idx) => (
        subItem.isDivider ? (
          <Box key={idx} sx={{ my: 0.5 }}>
            <Divider sx={{ borderColor: 'rgba(255,215,0,0.4)', borderWidth: '1px' }} />
            <Typography
              sx={{
                color: '#FFD700',
                px: 2,
                py: 0.5,
                fontSize: '10px',
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '1px',
                opacity: 0.85,
              }}
            >
              {subItem.label}
            </Typography>
          </Box>
        ) : (
          <MenuItem
            key={idx}
            onClick={() => handleNavigation(subItem.route)}
            sx={{
              fontSize: '13px',
              py: 0.85,
              px: 2,
              gap: 1,
              transition: 'all 0.15s ease',
              backgroundColor: location.pathname === subItem.route ? 'rgba(255,215,0,0.18) !important' : 'transparent',
              borderLeft: location.pathname === subItem.route ? '3px solid #FFD700' : '3px solid transparent',
              color: location.pathname === subItem.route ? '#FFD700' : 'rgba(255,255,255,0.9)',
              fontWeight: location.pathname === subItem.route ? 600 : 400,
              '&:hover': {
                backgroundColor: 'rgba(255,255,255,0.1) !important',
                color: 'white',
                borderLeft: '3px solid rgba(255,215,0,0.5)',
                pl: 2.5,
              },
            }}
          >
            {subItem.icon && (
              <Box sx={{ display: 'flex', alignItems: 'center', opacity: 0.8, flexShrink: 0 }}>
                {subItem.icon}
              </Box>
            )}
            {subItem.label}
          </MenuItem>
        )
      ))}
    </Menu>
  );

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', height: '48px', gap: 0 }}>
      {menuItems.map((item) => {
        if (!item.permission) return null;

        if (item.route && !item.subItems) {
          return (
            <Box
              key={item.id}
              component={NavLink}
              to={item.route}
              sx={{
                display: 'flex', alignItems: 'center', gap: 0.5, px: 1.8, py: 1,
                color: 'rgba(255,255,255,0.88)', textDecoration: 'none', cursor: 'pointer',
                fontSize: '13px', fontWeight: 500, height: '100%',
                borderBottom: '2px solid transparent',
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: 'rgba(255,255,255,0.1)',
                  color: 'white',
                },
                '&.active': {
                  backgroundColor: 'rgba(255,255,255,0.12)',
                  borderBottom: '2px solid #FFD700',
                  color: 'white',
                },
              }}
            >
              {item.icon}
              <Typography sx={{ fontSize: '13px', fontWeight: 500 }}>{item.label}</Typography>
            </Box>
          );
        }

        const active = isMenuActive(item);
        const isOpen = activeMenu === item.id;

        return (
          <Box key={item.id}>
            <Box
              onClick={(e) => handleMenuOpen(e, item.id)}
              sx={{
                display: 'flex', alignItems: 'center', gap: 0.5, px: 1.8, py: 1,
                color: 'rgba(255,255,255,0.88)', cursor: 'pointer', fontSize: '13px',
                fontWeight: 500, height: '48px',
                borderBottom: active || isOpen ? '2px solid #FFD700' : '2px solid transparent',
                backgroundColor: active || isOpen ? 'rgba(255,255,255,0.12)' : 'transparent',
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: 'rgba(255,255,255,0.1)',
                  color: 'white',
                },
              }}
            >
              {item.icon}
              <Typography sx={{ fontSize: '13px', fontWeight: 500 }}>{item.label}</Typography>
              <KeyboardArrowDown sx={{
                fontSize: 16, ml: 0.3,
                transition: 'transform 0.2s ease',
                transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
              }} />
            </Box>

            <Menu
              anchorEl={anchorEl}
              open={isOpen}
              onClose={handleMenuClose}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              transformOrigin={{ vertical: 'top', horizontal: 'left' }}
              TransitionProps={{ timeout: 180 }}
              PaperProps={{
                elevation: 8,
                sx: {
                  backgroundColor: '#1e2d5a',
                  color: 'white',
                  minWidth: '210px',
                  mt: 0,
                  borderRadius: '0 0 10px 10px',
                  border: '1px solid rgba(255,255,255,0.07)',
                  borderTop: 'none',
                  boxShadow: '0 8px 32px rgba(0,0,0,0.45)',
                },
              }}
            >
              {item.subItems?.map((subItem, idx) => (
                subItem.isNested ? (
                  <Box key={idx}>
                    <MenuItem
                      onClick={(e) => handleNestedMenuOpen(e, subItem.label)}
                      sx={{
                        fontSize: '13px', py: 1, px: 2, display: 'flex', justifyContent: 'space-between',
                        transition: 'all 0.15s ease',
                        backgroundColor: subItem.subItems?.some(s => location.pathname === s.route)
                          ? 'rgba(255,215,0,0.15) !important'
                          : 'transparent',
                        borderLeft: subItem.subItems?.some(s => location.pathname === s.route)
                          ? '3px solid #FFD700'
                          : '3px solid transparent',
                        color: subItem.subItems?.some(s => location.pathname === s.route) ? '#FFD700' : 'rgba(255,255,255,0.9)',
                        fontWeight: subItem.subItems?.some(s => location.pathname === s.route) ? 600 : 400,
                        '&:hover': {
                          backgroundColor: 'rgba(255,255,255,0.1) !important',
                          color: 'white',
                          borderLeft: '3px solid rgba(255,215,0,0.5)',
                        },
                        ...(activeNestedMenu === subItem.label && {
                          backgroundColor: 'rgba(255,255,255,0.1) !important',
                          borderLeft: '3px solid rgba(255,215,0,0.6)',
                        }),
                      }}
                    >
                      {subItem.label}
                      <KeyboardArrowRight sx={{
                        fontSize: 16,
                        transition: 'transform 0.15s ease',
                        transform: activeNestedMenu === subItem.label ? 'translateX(3px)' : 'none',
                      }} />
                    </MenuItem>
                    {renderNestedMenu(subItem)}
                  </Box>
                ) : (
                  <MenuItem
                    key={idx}
                    onClick={() => handleNavigation(subItem.route)}
                    sx={{
                      fontSize: '13px', py: 1, px: 2,
                      transition: 'all 0.15s ease',
                      backgroundColor: location.pathname === subItem.route ? 'rgba(255,215,0,0.18) !important' : 'transparent',
                      borderLeft: location.pathname === subItem.route ? '3px solid #FFD700' : '3px solid transparent',
                      color: location.pathname === subItem.route ? '#FFD700' : 'rgba(255,255,255,0.9)',
                      fontWeight: location.pathname === subItem.route ? 600 : 400,
                      '&:hover': {
                        backgroundColor: 'rgba(255,255,255,0.1) !important',
                        color: 'white',
                        borderLeft: '3px solid rgba(255,215,0,0.5)',
                      },
                    }}
                  >
                    {subItem.label}
                  </MenuItem>
                )
              ))}
            </Menu>
          </Box>
        );
      })}
    </Box>
  );
};

export default HorizontalNavbar;
